import { z } from 'zod';

// Document kinds. Each gates different visibility + sign-off rules:
//   kyc            : KYC pack — visible to staff + the client
//   deliverable    : in-flight working files — staff-visible until visible_to_client flipped
//   final_report   : capstone reports — REQUIRE dual sign-off (lead + compliance)
//                    before visible_to_client can be true
//   engagement_letter / other : free-form metadata, lead may flip visibility
export const DOCUMENT_KINDS = ['kyc', 'deliverable', 'final_report', 'engagement_letter', 'other'] as const;
export const DocumentKindSchema = z.enum(DOCUMENT_KINDS);
export type DocumentKind = z.infer<typeof DocumentKindSchema>;

// Lifecycle.
//   uploaded                    : file stored, staff-visible only
//   pending_lead_signoff        : final_report awaiting engagement lead
//   pending_compliance_signoff  : final_report awaiting compliance review
//   approved                    : visible_to_client may now be true
//   archived                    : superseded by a newer version; kept for audit
export const DOCUMENT_STATUSES = ['uploaded', 'pending_lead_signoff', 'pending_compliance_signoff', 'approved', 'archived'] as const;
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

// KYC per-document lifecycle (the authoritative state for `kind='kyc'` rows).
// The legacy `status` field stays at default for KYC rows and is meaningless
// there — the dual-sign-off pipeline (REQ-011) uses `status` for `final_report`
// rows only. See models/document.ts schema comment for the full discriminator.
export const KYC_REVIEW_STATUSES = [
  'uploaded',
  'in_review',
  'accepted',
  'rejected',
  'superseded',
] as const;
export const KycReviewStatusSchema = z.enum(KYC_REVIEW_STATUSES);
export type KycReviewStatus = z.infer<typeof KycReviewStatusSchema>;

// Upload input. The actual file binary isn't carried over the API (yet) —
// when S3 ships in Phase 5, this will accept a pre-signed PUT URL workflow.
// For now we record metadata only and the storage_url field is a placeholder.
//
// NOTE: this is the staff-side input. The portal KYC route (Phase B) has its
// own narrower input schema in `kyc_consent.ts` / `kyc-requirements.ts`-adjacent
// definitions — it must NOT accept `kyc_review_status`, `retention_until`,
// `reviewed_by_staff_id`, `reviewed_at`, `superseded_by`, `content_md5_b64`,
// or `rejected_reason` from the wire (those are server-derived).
export const DocumentUploadInputSchema = z.object({
  kind: DocumentKindSchema,
  // Either request_id (preferred) or client_id must be set. KYC docs may have
  // only client_id; deliverables are always tied to a brief.
  request_id: z.string().optional(),
  client_id: z.string().optional(),
  title: z.string().min(1).max(200),
  filename: z.string().min(1).max(200),
  mime_type: z.string().min(1).max(120).default('application/octet-stream'),
  size_bytes: z.number().int().nonnegative().default(0),
  // External storage handle — for S3, this is the object key. For now any opaque string.
  storage_url: z.string().min(1).max(2000),
  notes: z.string().max(2000).optional(),
});
export type DocumentUploadInput = z.infer<typeof DocumentUploadInputSchema>;

export const DocumentSignoffSchema = z.object({
  // One of: 'lead' | 'compliance'. Server checks the actor has the right role.
  who: z.enum(['lead', 'compliance']),
});

export const DocumentDtoSchema = z.object({
  id: z.string(),
  kind: DocumentKindSchema,
  status: DocumentStatusSchema,

  request_id: z.string().nullable().optional(),
  client_id: z.string().nullable().optional(),
  client_name: z.string().optional(),

  title: z.string(),
  filename: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  storage_url: z.string(),
  notes: z.string().nullable().optional(),

  visible_to_client: z.boolean(),

  lead_signed_by: z.string().nullable().optional(),
  lead_signed_at: z.string().nullable().optional(),
  compliance_signed_by: z.string().nullable().optional(),
  compliance_signed_at: z.string().nullable().optional(),

  // Uploader fields. `uploaded_by_staff_id` was previously required; the
  // portal-kyc feature relaxes it because client-uploaded KYC has no staff
  // uploader. Either field is set per row, never both.
  uploaded_by_staff_id: z.string().nullable().default(null),
  uploaded_by_user_id: z.string().nullable().default(null),

  // KYC-only fields (null for non-KYC rows).
  kyc_review_status: KycReviewStatusSchema.nullable().default(null),
  kyc_category: z.string().nullable().default(null),
  rejected_reason: z.string().nullable().default(null),
  reviewed_by_staff_id: z.string().nullable().default(null),
  reviewed_at: z.string().nullable().default(null),
  retention_until: z.string().nullable().default(null),
  superseded_by: z.string().nullable().default(null),
  content_md5_b64: z.string().nullable().default(null),

  created_at: z.string(),
  updated_at: z.string(),
});
export type DocumentDto = z.infer<typeof DocumentDtoSchema>;
